import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { titles, expRules } from '../types/level-system';
import { STORAGE_KEYS, getFromStorage, saveToStorage, clearStorage } from '../utils/storage';

interface UserState {
  exp: number;
  level: number;
  title: string;
  achievements: number[];
  streak: number;
  correctStreak: number;
  totalCorrect: number;
  lastLoginDate: string;
  name: string;
  school: string;
  currentLevel: number;
  currentQuestion: number;
  score: number;
  answers: number[];
  highestScore: number;
  errorCount: number;
  answeredQuestions: number[];
}

// 从本地存储获取初始状态
const savedUserInfo = getFromStorage(STORAGE_KEYS.USER_INFO);

const initialState: UserState = {
  exp: 0,
  level: 1,
  title: titles[0].name,
  achievements: [],
  streak: 0,
  correctStreak: 0,
  totalCorrect: 0,
  lastLoginDate: new Date().toDateString(),
  name: '',
  school: '',
  currentLevel: 0,
  currentQuestion: 0,
  score: 0,
  answers: [],
  highestScore: 0,
  errorCount: 0,
  answeredQuestions: [],
  ...savedUserInfo // 合并保存的用户信息
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    addExp: (state, action: PayloadAction<number>) => {
      state.exp += action.payload;
      // 更新等级和称号
      for (let i = titles.length - 1; i >= 0; i--) {
        if (state.exp >= titles[i].requiredExp) {
          state.level = titles[i].id;
          state.title = titles[i].name;
          break;
        }
      }
    },
    answerQuestion: (state, action: PayloadAction<{
      correct: boolean,
      difficulty: 'easy' | 'medium' | 'hard'
    }>) => {
      const { correct, difficulty } = action.payload;
      
      if (correct) {
        state.exp += expRules.questionExp[difficulty];
        state.correctStreak++;
        state.totalCorrect++;
        
        if (state.correctStreak in expRules.comboExp) {
          state.exp += expRules.comboExp[state.correctStreak as keyof typeof expRules.comboExp];
        }
      } else {
        state.correctStreak = 0;
      }
    },
    checkDailyLogin: (state) => {
      const today = new Date().toDateString();
      if (state.lastLoginDate !== today) {
        state.exp += expRules.dailyLoginExp;
        if (new Date(state.lastLoginDate).getTime() === new Date(today).getTime() - 86400000) {
          state.streak++;
        } else {
          state.streak = 1;
        }
        state.lastLoginDate = today;
      }
    },
    unlockAchievement: (state, action: PayloadAction<number>) => {
      if (!state.achievements.includes(action.payload)) {
        state.achievements.push(action.payload);
      }
    },
    setUserInfo: (state, action: PayloadAction<{ name: string; school: string }>) => {
      state.name = action.payload.name;
      state.school = action.payload.school;
      saveToStorage(STORAGE_KEYS.USER_INFO, {
        name: action.payload.name,
        school: action.payload.school
      });
    },
    updateQuizProgress: (state, action: PayloadAction<{
      currentLevel: number;
      currentQuestion: number;
      score: number;
      answers: number[];
      errorCount: number;
      answeredQuestions: number[];
    }>) => {
      state.currentLevel = action.payload.currentLevel;
      state.currentQuestion = action.payload.currentQuestion;
      state.score = action.payload.score;
      state.answers = action.payload.answers;
      state.errorCount = action.payload.errorCount;
      state.answeredQuestions = action.payload.answeredQuestions;
      
      // 更新最高分
      if (action.payload.score > state.highestScore) {
        state.highestScore = action.payload.score;
      }
      
      // 保存到 localStorage
      saveToStorage(STORAGE_KEYS.QUIZ_STATE, {
        currentLevel: action.payload.currentLevel,
        currentQuestion: action.payload.currentQuestion,
        score: action.payload.score,
        answers: action.payload.answers,
        errorCount: action.payload.errorCount,
      });
    },
    resetQuizProgress: (state) => {
      state.currentLevel = 0;
      state.currentQuestion = 0;
      state.score = 0;
      state.answers = [];
      state.errorCount = 0;
      state.answeredQuestions = [];
      clearStorage(STORAGE_KEYS.QUIZ_STATE);
    }
  }
});

export const { 
  addExp, 
  answerQuestion, 
  checkDailyLogin, 
  unlockAchievement, 
  setUserInfo,
  updateQuizProgress,
  resetQuizProgress 
} = userSlice.actions;

export default userSlice.reducer; 
